/**
 * ProductManagement.jsx
 * 製品管理メインコンポーネント（分割後）
 * フェーズ1: 基本単価機能（税抜き標準価格）
 * PWA対応: ボトムナビゲーションとの重複回避
 */

import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Container, 
  Typography, 
  Paper, 
  Button, 
  Tabs,
  Tab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  Alert,
  CircularProgress,
  Divider
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ScienceIcon from '@mui/icons-material/Science';
import WarningIcon from '@mui/icons-material/Warning';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/Header';
import { checkAuthAndRedirect } from '../../api/auth';
import axios from 'axios';

// 分割されたコンポーネントとユーティリティのインポート
import ProductForm from './ProductForm';
import ProductList from './ProductList';
import { 
  validateStandardUnitPrice, 
  normalizeNumericInput, 
  preparePriceForAPI,
  formatPrice 
} from './priceValidationUtils';

// バリデーション定数
const MAX_NAME_LENGTH = 50;
const FORBIDDEN_REGEX = /[<>"'`;\\\/*?&|^$()[\]{}:\x00-\x1F\x7F]/;

const ProductManagement = () => {
  const [loading, setLoading] = useState(false);
  const [products, setProducts] = useState([]);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [tabValue, setTabValue] = useState(0);
  
  // 2段階削除確認のステート
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleteStep, setDeleteStep] = useState(1);
  const [productToDelete, setProductToDelete] = useState(null);
  
  const navigate = useNavigate();
  
  // プラン制限のステート
  const [planLimits, setPlanLimits] = useState({
    maxProducts: 0,
    currentProducts: 0
  });
  
  // 製品情報のステート（標準価格フィールド追加）
  const [formData, setFormData] = useState({
    id: null,
    name: '',
    unit: 'L',
    standardUnitPrice: ''
  });

  // 入力検証エラー（標準価格エラー追加）
  const [errors, setErrors] = useState({
    name: '',
    unit: '',
    standardUnitPrice: ''
  });

  // プラン情報を取得する関数
  const fetchPlanLimits = async () => {
    try {
      const token = sessionStorage.getItem("token");
      if (!token) {
        navigate('/login');
        return;
      }

      const config = { headers: { Authorization: `Bearer ${token}` } };
      
      const userResponse = await axios.get('/api/auth/me', config);
      const plan = userResponse.data.plan;
      
      const productResponse = await axios.get('/api/products', config);
      
      setPlanLimits({
        maxProducts: plan.maxProducts || (plan.name === 'Standard' ? 10 : 1),
        currentProducts: productResponse.data.length
      });
      
    } catch (err) {
      setError('プラン情報の取得に失敗しました。ネットワーク接続を確認してください。');
    }
  };

  // 製品データを取得
  const fetchProducts = async () => {
    setLoading(true);
    try {
      const token = sessionStorage.getItem("token");
      if (!token) {
        navigate('/login');
        return;
      }

      const config = { headers: { Authorization: `Bearer ${token}` } };
      const response = await axios.get('/api/products', config);
      
      setProducts(response.data);
      setError('');
    } catch (err) {
      setError('製品情報の取得に失敗しました。ネットワーク接続を確認してください。');
      if (err.response && (err.response.status === 401 || err.response.status === 403)) {
        navigate('/login');
      }
    } finally {
      setLoading(false);
    }
  };

  // 初期データの読み込み
  useEffect(() => {
    const loadData = async () => {
      try {
        await checkAuthAndRedirect(navigate);
        await Promise.all([
          fetchProducts(),
          fetchPlanLimits()
        ]);
      } catch (error) {
        // エラーハンドリング
      }
    };
    
    loadData();
  }, [navigate]);

  // タブ切り替え
  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
    resetForm();
  };

  // フォームリセット
  const resetForm = () => {
    setFormData({
      id: null,
      name: '',
      unit: 'L',
      standardUnitPrice: ''
    });
    setErrors({
      name: '',
      unit: '',
      standardUnitPrice: ''
    });
  };

  // 製品編集モードへ
  const handleEdit = (product) => {
    setFormData({
      ...product,
      standardUnitPrice: product.standardUnitPrice ? product.standardUnitPrice.toString() : ''
    });
    setTabValue(1);
  };

  // 標準価格入力ハンドラー
  const handleStandardUnitPriceChange = (e) => {
    const value = e.target.value;
    
    // 入力値の前処理（全角数字を半角に変換）
    const normalizedValue = normalizeNumericInput(value);
    
    const error = validateStandardUnitPrice(normalizedValue);
    setFormData(prev => ({ ...prev, standardUnitPrice: normalizedValue }));
    setErrors(prev => ({ ...prev, standardUnitPrice: error || '' }));
  };

  // フォーム入力ハンドラー - 名前フィールド
  const handleNameChange = (e) => {
    const value = e.target.value;
    let nameError = '';
    
    if (value.length > MAX_NAME_LENGTH) {
      nameError = `製品名は${MAX_NAME_LENGTH}文字以内で入力してください。`;
    } else if (FORBIDDEN_REGEX.test(value)) {
      nameError = "使用できない文字が含まれています（< > \" ' ` ; \\ / * ? & など）。";
    }
    
    setFormData(prev => ({ ...prev, name: value }));
    setErrors(prev => ({ ...prev, name: nameError }));
  };

  // 単位選択ハンドラー
  const handleUnitChange = (e) => {
    const value = e.target.value;
    setFormData(prev => ({ ...prev, unit: value }));
    setErrors(prev => ({ ...prev, unit: '' }));
  };

  // フォーム送信時の検証
  const validateForm = () => {
    let valid = true;
    const newErrors = { ...errors };
    
    // 名前の検証
    if (!formData.name.trim()) {
      newErrors.name = '製品名を入力してください';
      valid = false;
    } else if (formData.name.length > MAX_NAME_LENGTH) {
      newErrors.name = `製品名は${MAX_NAME_LENGTH}文字以内で入力してください。`;
      valid = false;
    } else if (FORBIDDEN_REGEX.test(formData.name)) {
      newErrors.name = "使用できない文字が含まれています（< > \" ' ` ; \\ / * ? & など）。";
      valid = false;
    }
    
    // 単位の検証
    if (!formData.unit) {
      newErrors.unit = '単位を選択してください';
      valid = false;
    }
    
    // 標準価格の検証
    const priceError = validateStandardUnitPrice(formData.standardUnitPrice);
    if (priceError) {
      newErrors.standardUnitPrice = priceError;
      valid = false;
    }
    
    setErrors(newErrors);
    return valid;
  };

  // フォーム送信ハンドラー
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    // 新規登録の場合、プラン制限チェック
    if (!formData.id && planLimits.currentProducts >= planLimits.maxProducts) {
      setError(`プランの製品登録上限（${planLimits.maxProducts}件）に達しています。より多くの製品を登録するには、プランをアップグレードしてください。`);
      return;
    }
    
    setLoading(true);
    
    try {
      const token = sessionStorage.getItem("token");
      const config = { headers: { Authorization: `Bearer ${token}` } };
      
      // APIリクエストデータの準備
      const requestData = {
        name: formData.name,
        unit: formData.unit,
        standardUnitPrice: preparePriceForAPI(formData.standardUnitPrice)
      };
      
      let response;
      
      if (formData.id) {
        // 更新
        response = await axios.put(`/api/products/${formData.id}`, requestData, config);
        setProducts(prev => prev.map(p => p.id === formData.id ? response.data : p));
        setSuccess(`製品「${formData.name}」を更新しました`);
      } else {
        // 新規登録
        response = await axios.post('/api/products', requestData, config);
        const newProducts = [...products, response.data];
        setProducts(newProducts);
        
        setPlanLimits(prev => ({
          ...prev,
          currentProducts: prev.currentProducts + 1
        }));
        
        setSuccess(`製品「${formData.name}」を登録しました`);
      }
      
      resetForm();
      setTabValue(0);
    } catch (err) {
      if (err.response && err.response.data) {
        setError(`製品の登録に失敗しました: ${err.response.data}`);
      } else {
        setError('製品の登録に失敗しました。もう一度お試しください。');
      }
      
      if (err.response && (err.response.status === 401 || err.response.status === 403)) {
        navigate('/login');
      }
    } finally {
      setLoading(false);
    }
  };

  // キャンセルハンドラー
  const handleCancel = () => {
    resetForm();
    if (products.length > 0) {
      setTabValue(0);
    }
  };

  // 製品削除確認ダイアログを表示（第1段階）
  const handleDeleteConfirm = (product) => {
    setProductToDelete(product);
    setDeleteStep(1);
    setConfirmDelete(true);
  };

  // 第1段階から第2段階へ
  const handleDeleteFirstStepNext = () => {
    setDeleteStep(2);
  };

  // 第2段階から第1段階に戻る
  const handleDeleteBack = () => {
    setDeleteStep(1);
  };

  // 削除ダイアログを閉じる
  const handleDeleteDialogClose = () => {
    if (!loading) {
      setConfirmDelete(false);
      setDeleteStep(1);
      setProductToDelete(null);
    }
  };

  // 製品削除実行（第2段階）
  const handleDelete = async () => {
    if (!productToDelete) return;
    
    setLoading(true);
    
    try {
      const token = sessionStorage.getItem("token");
      const config = { headers: { Authorization: `Bearer ${token}` } };
      await axios.delete(`/api/products/${productToDelete.id}`, config);
      
      const newProducts = products.filter(p => p.id !== productToDelete.id);
      setProducts(newProducts);
      
      setPlanLimits(prev => ({
        ...prev,
        currentProducts: prev.currentProducts - 1
      }));
      
      setSuccess(`製品「${productToDelete.name}」を削除しました`);
      handleDeleteDialogClose();
    } catch (err) {
      setError('製品の削除に失敗しました。もう一度お試しください。');
    } finally {
      setLoading(false);
    }
  };

  // スナックバーを閉じる
  const handleSnackbarClose = () => {
    setSuccess(false);
    setError('');
  };

  // 製品登録が制限に達しているかどうか
  const isProductLimitReached = planLimits.currentProducts >= planLimits.maxProducts;

  return (
    <Box sx={{ bgcolor: '#F5F7FA', minHeight: '100vh' }}>
      <Header />
      
      <Container maxWidth="md" sx={{ mt: 4 }}>
        {/* ページタイトル */}
        <Paper 
          elevation={0}
          sx={{ 
            p: 3, 
            mb: 4, 
            borderRadius: 2,
            background: 'linear-gradient(to right, #ff9800, #ff7b00)',
            color: 'white'
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <ScienceIcon sx={{ fontSize: 32 }} />
            <Box sx={{ flex: 1 }}>
              <Typography variant="h5" component="h1" sx={{ fontWeight: 600 }}>
                製品管理
              </Typography>
              <Typography variant="subtitle1">
                製品情報の登録・管理
              </Typography>
            </Box>
          </Box>
        </Paper>

        {/* 戻るボタン */}
        <Box sx={{ mb: 3 }}>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/data-registration')}
            sx={{ color: 'text.secondary' }}
          >
            データ登録メニューに戻る
          </Button>
        </Box>

        {/* タブ切り替え */}
        <Paper elevation={2} sx={{ borderRadius: 2, overflow: 'hidden' }}>
          <Tabs
            value={tabValue}
            onChange={handleTabChange}
            variant="fullWidth"
            sx={{ borderBottom: 1, borderColor: 'divider' }}
          >
            <Tab label="製品一覧" />
            <Tab label="製品登録・編集" />
          </Tabs>
          
          {/* 製品一覧タブ */}
          <Box sx={{ display: tabValue === 0 ? 'block' : 'none' }}>
            <ProductList
              products={products}
              loading={loading}
              planLimits={planLimits}
              onEdit={handleEdit}
              onDeleteConfirm={handleDeleteConfirm}
              onAddProduct={() => setTabValue(1)}
            />
          </Box>
          
          {/* 製品登録・編集タブ */}
          <Box sx={{ display: tabValue === 1 ? 'block' : 'none' }}>
            <ProductForm
              formData={formData}
              errors={errors}
              loading={loading}
              isProductLimitReached={isProductLimitReached}
              planLimits={planLimits}
              onSubmit={handleSubmit}
              onCancel={handleCancel}
              onNameChange={handleNameChange}
              onUnitChange={handleUnitChange}
              onStandardUnitPriceChange={handleStandardUnitPriceChange}
            />
          </Box>
        </Paper>

        {/* 🚀 PWA対応: ボトムナビゲーション用の安全マージンを追加 */}
        <Box sx={{ 
          height: { xs: 120, sm: 60 }, // モバイル120px、デスクトップ60px
          width: '100%'
        }} />
      </Container>

      {/* 2段階削除確認ダイアログ */}
      <Dialog
        open={confirmDelete}
        onClose={handleDeleteDialogClose}
        maxWidth="sm"
        fullWidth
      >
        {deleteStep === 1 ? (
          // 第1段階：基本確認
          <>
            <DialogTitle sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: 1,
              color: 'warning.main' 
            }}>
              <WarningIcon />
              製品削除の確認
            </DialogTitle>
            
            <DialogContent>
              <Typography variant="body1" sx={{ mb: 2 }}>
                以下の製品を削除しようとしています：
              </Typography>
              
              <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1, mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <ScienceIcon color="primary" />
                  <Typography variant="h6" color="text.primary">
                    {productToDelete?.name}
                  </Typography>
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  単位: {productToDelete?.unit}
                </Typography>
                {/* 標準価格も表示（フォーマット適用） */}
                {productToDelete?.standardUnitPrice && (
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    標準価格: ¥{formatPrice(productToDelete.standardUnitPrice)}（税抜き）
                  </Typography>
                )}
              </Box>

              <Alert severity="warning" sx={{ mb: 2 }}>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  <strong>削除の影響：</strong>
                </Typography>
                <ul style={{ margin: 0, paddingLeft: '20px' }}>
                  <li>この製品に関連する顧客データは保持されます</li>
                  <li>今後この製品での新規設定はできなくなります</li>
                  <li>設定されている標準価格情報も削除されます</li>
                  <li>この操作は元に戻すことができません</li>
                </ul>
              </Alert>

              <Typography variant="body2" color="text.secondary">
                この操作は慎重に行ってください。続行しますか？
              </Typography>
            </DialogContent>
            
            <DialogActions>
              <Button onClick={handleDeleteDialogClose} disabled={loading}>
                キャンセル
              </Button>
              <Button 
                onClick={handleDeleteFirstStepNext} 
                color="warning"
                variant="outlined"
                disabled={loading}
              >
                削除の詳細を確認
              </Button>
            </DialogActions>
          </>
        ) : (
          // 第2段階：最終確認
          <>
            <DialogTitle sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: 1,
              color: 'error.main' 
            }}>
              <DeleteForeverIcon />
              最終確認 - 削除実行
            </DialogTitle>
            
            <DialogContent>
              <Alert severity="error" sx={{ mb: 3 }}>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  <strong>重要：この操作により以下が実行されます</strong>
                </Typography>
                <ul style={{ margin: 0, paddingLeft: '20px' }}>
                  <li>製品「{productToDelete?.name}」が削除されます</li>
                  <li>関連する顧客の製品設定は影響を受けません</li>
                  <li>標準価格設定も完全に削除されます</li>
                  <li>この操作は元に戻すことができません</li>
                </ul>
              </Alert>

              <Divider sx={{ my: 2 }} />

              <Box sx={{ p: 2, bgcolor: 'rgba(244, 67, 54, 0.05)', borderRadius: 1 }}>
                <Typography variant="body2" color="error.main" sx={{ fontWeight: 'medium' }}>
                  削除を実行する場合は、下の「完全に削除する」ボタンをクリックしてください。
                </Typography>
              </Box>
            </DialogContent>
            
            <DialogActions>
              <Button onClick={handleDeleteBack} disabled={loading}>
                戻る
              </Button>
              <Button onClick={handleDeleteDialogClose} disabled={loading}>
                キャンセル
              </Button>
              <Button 
                onClick={handleDelete}
                color="error"
                variant="contained"
                disabled={loading}
                startIcon={loading ? <CircularProgress size={20} /> : <DeleteForeverIcon />}
              >
                {loading ? '削除中...' : '完全に削除する'}
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* 成功・エラーメッセージ */}
      <Snackbar open={success || error !== ''} autoHideDuration={6000} onClose={handleSnackbarClose}>
        <Alert 
          onClose={handleSnackbarClose} 
          severity={success ? "success" : "error"} 
          sx={{ width: '100%' }}
        >
          {success || error}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ProductManagement;